import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';

import NoDataForSelection from 'components/atoms/NoDataForSelection';
import PackArchitectureChartWithControls from 'components/molecules/PackArchitecture/ReusableBubbleChart';
import PackArchitectureTable from 'components/molecules/PackArchitecture/PackArchitectureTable';
import { usePackArchitectureChartToTableScrollSync } from 'components/molecules/PackArchitecture/usePackArchitectureChartToTableScrollSync';
import {
    collectConfiguredPackArchitectureKpiIds,
    createPackArchitectureKpiIdMatcher,
} from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';

import {
    AXIS_PREVIEW_SALES_KPI_IDS,
    buildPackArchitectureChartTitle,
    buildPackArchitectureChartTitleSegments,
    getAxisLabel,
    type PackArchitectureChartTitleInput,
} from './packArchitectureMeta.helpers';
import PackArchitectureExportModal from './PackArchitectureExportModal';
import { PackArchitectureBreadcrumb, PackArchitecturePageHeader } from './PackArchitecturePageHeader';
import PackArchitectureToolbar from './PackArchitectureToolbar';
import { usePackArchitectureExport } from './usePackArchitectureExport';

const ChartTitle = () => {
    const chartTitleInput = usePackArchitectureChartTitleInput();
    const titleSegments = useMemo(() => buildPackArchitectureChartTitleSegments(chartTitleInput), [chartTitleInput] );

    return (
        <>
            {titleSegments.map((segment, index) =>
                segment.emphasis ? (
                    <span key={`${segment.text}-${index}`} className="font-semibold">
                        {segment.text}
                    </span>
                ) : (
                    <span key={`${segment.text}-${index}`}>{segment.text}</span>
                ),
            )}
        </>
    );
};

const usePackArchitectureChartTitleInput = (): PackArchitectureChartTitleInput => {
    const { xAxis, xAxisConfig, xAxisOptionGroups, yAxis, yAxisOptionGroups, bubbleRepresents, bubbleRepresentsOptionGroups } = usePackArchitectureContext();

    return useMemo(() => ({ xAxis,xAxisConfig,xAxisOptionGroups,yAxis,yAxisOptionGroups,bubbleRepresents,bubbleRepresentsOptionGroups }),
        [ xAxis, xAxisConfig, xAxisOptionGroups, yAxis, yAxisOptionGroups, bubbleRepresents, bubbleRepresentsOptionGroups ]
    );
};

const PackArchitecture = () => {
    const {
        title,
        tooltip,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        bubbleSize,
        bubbleSizeOptionGroups,
        showHeatMap,
        tableKpiOptionItems,
        rows,
        chartRequest,
        totalCount,
        hasChartData,
        isChartPanelLoading,
        showNoFilterSelection,
        clearSelection,
        tableRows,
        isChartPaginatedLoading,
        isTablePaginatedLoading,
    } = usePackArchitectureContext();

    const chartHorizontalScrollRef = useRef<HTMLDivElement | null>(null);
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const isSyncingTableFromChartRef = useRef(false);
    /** Table column pagination shares chart data — block chart→table snap until table restores scroll. */
    const suppressChartToTableSyncRef = useRef(false);
    const hasTableColumns = tableRows.length > 0;

    usePackArchitectureChartToTableScrollSync({
        chartScrollRef: chartHorizontalScrollRef,
        tableWrapperRef,
        isSyncingTableFromChartRef,
        suppressChartToTableSyncRef,
        chartColumnCount: rows.length,
        isChartPaginatedLoading,
        isTablePaginatedLoading,
        tableLoadedColumnCount: tableRows.length,
        // Re-sync when chart columns change or table first mounts — not on table column pagination.
        tableSyncKey: rows.length + (hasTableColumns ? 1 : 0),
    });

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const interactivePanelRef = useRef<HTMLDivElement>(null);
    const chartAreaRef = useRef<HTMLDivElement>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>;

    const xAxisLabel = useMemo(() => getAxisLabel(xAxis, xAxisOptionGroups), [xAxis, xAxisOptionGroups]);
    const yAxisLabel = useMemo(() => getAxisLabel(yAxis, yAxisOptionGroups), [yAxis, yAxisOptionGroups]);
    const bubbleRepresentsLabel = useMemo(
        () => getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
        [bubbleRepresents, bubbleRepresentsOptionGroups],
    );
    const bubbleSizeLabel = useMemo(
        () => getAxisLabel(bubbleSize, bubbleSizeOptionGroups),
        [bubbleSize, bubbleSizeOptionGroups],
    );

    const chartTitleInput = usePackArchitectureChartTitleInput();
    const chartTitle = useMemo(() => buildPackArchitectureChartTitle(chartTitleInput),[chartTitleInput]);

    const kpiIdMatcher = useMemo(
        () =>
            createPackArchitectureKpiIdMatcher({
                configuredKpiIds: collectConfiguredPackArchitectureKpiIds(
                    yAxisOptionGroups,
                    tableKpiOptionItems,
                    AXIS_PREVIEW_SALES_KPI_IDS,
                ),
                salesKpiIds: AXIS_PREVIEW_SALES_KPI_IDS,
            }),
        [tableKpiOptionItems, yAxisOptionGroups],
    );

    const { isExporting, handleExport } = usePackArchitectureExport({
        chartTitle,
        xAxisLabel,
        yAxis,
        yAxisLabel,
        bubbleRepresentsLabel,
        bubbleSizeLabel,
        showHeatMap,
        kpiIdMatcher,
        rows,
        chartRequest,
        totalCount,
        onExportSuccess: () => setIsExportModalOpen(false),
    });

    const handleOpenExportModal = useCallback(() => setIsExportModalOpen(true), []);
    const handleCloseExportModal = useCallback(() => {
        if (isExporting) return;
        setIsExportModalOpen(false);
    }, [isExporting]);

    useEffect(() => {
        const handleOutsidePointerDown = (event: MouseEvent) => {
            if (interactivePanelRef.current && !interactivePanelRef.current.contains(event.target as Node)) {
                clearSelection();
            }
        };
        document.addEventListener('mousedown', handleOutsidePointerDown);
        return () => document.removeEventListener('mousedown', handleOutsidePointerDown);
    }, [clearSelection]);

    const handleInteractivePanelClick = useCallback(
        (event: React.MouseEvent) => {
            const target = event.target as Node;
            if (tableContainerRef.current?.contains(target)) return;
            if (chartAreaRef.current?.contains(target)) return;
            clearSelection();
        },
        [clearSelection],
    );

    return (
        <div className="flex flex-col gap-2 py-2">
            <PackArchitectureBreadcrumb title={title} />

            <div
                ref={interactivePanelRef}
                className="rounded-lg bg-white p-4"
                onClick={handleInteractivePanelClick}
            >
                <PackArchitecturePageHeader
                    title={title}
                    tooltip={tooltip}
                    onDownloadClick={handleOpenExportModal}
                    downloadDisabled={!hasChartData || isChartPanelLoading}
                />

                {showNoFilterSelection ? (
                    <div className="flex min-h-[420px] items-center justify-center rounded-md border border-borderLinePurpuleAlt bg-white py-12">
                        <NoDataForSelection />
                    </div>
                ) : (
                    <>
                        <PackArchitectureToolbar />

                        <div ref={chartAreaRef}>
                            <PackArchitectureChartWithControls
                                chartTitle={<ChartTitle />}
                                xAxisTitle={getAxisLabel(xAxis, xAxisOptionGroups)}
                                yAxisTitle={getAxisLabel(yAxis, yAxisOptionGroups)}
                                horizontalScrollRef={chartHorizontalScrollRef}
                            />
                        </div>

                        <PackArchitectureTable
                            tableContainerRef={tableContainerRef}
                            tableWrapperRef={tableWrapperRef}
                            isSyncingTableFromChartRef={isSyncingTableFromChartRef}
                            suppressChartToTableSyncRef={suppressChartToTableSyncRef}
                        />
                    </>
                )}
            </div>

            <PackArchitectureExportModal
                open={isExportModalOpen}
                exporting={isExporting}
                noData={!hasChartData}
                rowCount={totalCount}
                onClose={handleCloseExportModal}
                onExport={handleExport}
            />
        </div>
    );
};

export default PackArchitecture;
